import React, { FC, useState, useEffect } from "react";
import { Spin, Modal, Tabs, Typography } from "antd";
import { RoiLineChart, RoiBarChart } from "./roi-chart";
import { BEDROCK_GET_ROI_ANALYSIS_QUERY } from "~/graphql/query";
import { useQuery } from "@apollo/client";
import { RoiTimeSeriesData, prepareChartData } from "./chart-data";
import chartExplanations from "./chart-explanations";

// TODO JS: Disable AI insights if firm has AI switched off?

import styles from "./roi.css";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  ["charts-container"]: chartsContainerClassName,
  ["charts-row"]: chartsRowClassName,
  ["chart-container"]: chartContainerClassName,
} = styles as any;

function formatBigNumber(num: number): string {
  if (num < 1000000) {
    return `${num.toLocaleString()}`;
  } else {
    return `${(num / 1000000).toLocaleString()}M`;
  }
}

function toSignificantFigures(num: number, sigFigs: number) {
  if (num === 0) return 0;
  const magnitude = Math.floor(Math.log10(Math.abs(num)));
  const factor = Math.pow(10, magnitude - sigFigs + 1);
  return Math.round(num / factor) * factor;
}

type RoiProps = {
  loading: boolean;
  currencySymbol: string;
  practiceAreaId: number | null;
  officeLocationId: number | null;
  roiTimeSeriesData: RoiTimeSeriesData[];
  unfilteredRoiData: RoiTimeSeriesData[];
  timeIntervals: string[];
};

export const Roi: FC<RoiProps> = ({
  loading,
  currencySymbol,
  practiceAreaId,
  officeLocationId,
  roiTimeSeriesData,
  unfilteredRoiData,
  timeIntervals,
}) => {
  const [selectedChart, setSelectedChart] = useState<string | null>(null);
  const [chartDataReady, setChartDataReady] = useState<boolean>(false);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiRecommendations, setAiRecommendations] = useState<string[] | null>(null);

  const PA_LABEL = VPDLabel("PracticeArea");

  const chartData = prepareChartData(roiTimeSeriesData);
  const unfilteredChartData = prepareChartData(unfilteredRoiData);
  const vpdRoiTimeSeriesData = roiTimeSeriesData.filter(
    (item: RoiTimeSeriesData) => item.vpd !== null
  );

  useEffect(() => {
    const hasData = Object.values(unfilteredChartData).some((array) => array.length > 0);
    setChartDataReady(hasData);
  }, [unfilteredChartData]);

  const { data: roiAnalysisData, loading: analysisLoading } = useQuery(
    BEDROCK_GET_ROI_ANALYSIS_QUERY,
    {
      variables: {
        roiData: unfilteredChartData,
      },
      skip: !chartDataReady,
    }
  );

  useEffect(() => {
    if (roiAnalysisData && roiAnalysisData.bedrockRoiAnalyseAndRecommend) {
      setAiAnalysis(roiAnalysisData.bedrockRoiAnalyseAndRecommend.analysis);
      setAiRecommendations(roiAnalysisData.bedrockRoiAnalyseAndRecommend.recommendations);
    }
  }, [roiAnalysisData]);

  if (!loading && roiTimeSeriesData.length === 0) {
    return (
      <>
        <h1>No data</h1>
        <p>
          There is no data available for the selected {PA_LABEL}, Office Location and Time Range.
          Please try a different selection.
        </p>
      </>
    );
  }

  // const totalRevenue = toSignificantFigures(
  //   cumulativeVpdRevenueData[cumulativeVpdRevenueData.length - 1]?.y,
  //   2
  // );
  const totalProfit = toSignificantFigures(
    chartData.cumulativeVpdProfitData[chartData.cumulativeVpdProfitData.length - 1]?.y,
    2
  );
  // const potentialRevenue = toSignificantFigures(
  //   vpdRoiTimeSeriesData[vpdRoiTimeSeriesData.length - 1]?.cumulativePotentialRevenue ?? 0,
  //   2
  // );
  const potentialProfit = toSignificantFigures(
    vpdRoiTimeSeriesData[vpdRoiTimeSeriesData.length - 1]?.cumulativePotentialProfit ?? 0,
    2
  );
  // const totalRevenueString = isNaN(totalRevenue) ? "0" : totalRevenue.toLocaleString();
  const totalProfitString = isNaN(totalProfit) ? "0" : totalProfit.toLocaleString();
  // const potentialRevenueString = isNaN(potentialRevenue) ? "0" : potentialRevenue.toLocaleString();
  const potentialProfitString = isNaN(potentialProfit) ? "0" : potentialProfit.toLocaleString();
  const totalVpdMatters = chartData.vpdMatterCountData.reduce((sum, item) => sum + item.y, 0);

  const charts: { [key: string]: JSX.Element | null } = {
    // cumulativeVpdRevenue: (
    //   <RoiLineChart
    //     title={"Additional Cumulative Revenue Generated by VPD"}
    //     tickFormat={(tick) => `${currencySymbol}${formatBigNumber(tick)}`}
    //     data={[cumulativeVpdRevenueData]}
    //     timeIntervals={timeIntervals}
    //     vpdOnly
    //   />
    // ),
    cumulativeVpdProfit: (
      <RoiLineChart
        title={"Additional Cumulative Profit Generated by VPD"}
        tickFormat={(tick) => `${currencySymbol}${formatBigNumber(tick)}`}
        data={[chartData.cumulativeVpdProfitData]}
        timeIntervals={timeIntervals}
        vpdOnly
      />
    ),
    mattersCreated: (
      <RoiBarChart
        title={"Matters Created"}
        tickFormat={(tick) => `${tick}`}
        data={[chartData.externalMatterCountData, chartData.vpdMatterCountData]}
        timeIntervals={timeIntervals}
      />
    ),
    grossProfitMargin: (
      <RoiLineChart
        title={"Gross Profit Margin"}
        tickFormat={(tick) => `${tick}%`}
        data={[chartData.vpdGpmData, chartData.notVpdGpmData]}
        timeIntervals={timeIntervals}
        showLegend={true}
      />
    ),
    rollingAgreedRate: (
      <RoiLineChart
        title={"Rolling Agreed Rate"}
        tickFormat={(tick) => `${currencySymbol}${tick}`}
        data={[chartData.vpdRollingAgreedRateData, chartData.notVpdRollingAgreedRateData]}
        timeIntervals={timeIntervals}
      />
    ),
    rollingRecoveredRate: (
      <RoiLineChart
        title={"Rolling Recovered Rate"}
        tickFormat={(tick) => `${currencySymbol}${tick}`}
        data={[chartData.vpdRollingRecoveredRateData, chartData.notVpdRollingRecoveredRateData]}
        timeIntervals={timeIntervals}
        showLegend={true}
      />
    ),
    // writeOff: (
    //   <RoiLineChart
    //     title={"Write Off"}
    //     tickFormat={(tick) => `${currencySymbol}${tick}`}
    //     data={[chartData.vpdWriteOffData, chartData.notVpdWriteOffData]}
    //     timeIntervals={timeIntervals}
    //   />
    // ),
    writeOffPercentage: (
      <RoiLineChart
        title={"Write Off Percentage"}
        tickFormat={(tick) => `${tick}%`}
        data={[chartData.vpdWriteOffPercentageData, chartData.notVpdWriteOffPercentageData]}
        timeIntervals={timeIntervals}
        showLegend={true}
      />
    ),
    averageTotalCharge: (
      <RoiLineChart
        title={"Total Charge Per Matter"}
        tickFormat={(tick) => `${currencySymbol}${formatBigNumber(tick)}`}
        data={[chartData.vpdTotalChargeData, chartData.notVpdTotalChargeData]}
        timeIntervals={timeIntervals}
        showLegend={true}
      />
    ),
  };

  const handleChartClick = (chartName: string) => {
    setSelectedChart(chartName);
  };

  const handleModalClose = () => {
    setSelectedChart(null);
  };

  return loading ? (
    <div style={{ textAlign: "left", marginLeft: "50px", marginTop: "50px" }}>
      <Spin size="large" tip="Loading..." />
    </div>
  ) : (
    <>
      <div>
        <h1>Highlights</h1>
        <ul>
          <li>
            In the time frame selected, VPD has generated your firm approximately{" "}
            <b>
              {currencySymbol}
              {totalProfitString} in additional profit
            </b>
            {practiceAreaId || officeLocationId ? " in the selected department" : ""}.
          </li>
          <li>
            In the time frame selected, VPD <b>could</b> have generated your firm approximately{" "}
            <b>
              {currencySymbol}
              {potentialProfitString} in additional profit
            </b>
            {practiceAreaId || officeLocationId ? " in the selected department" : ""}.
          </li>
          <li>
            Total number of VPD matters: <b>{totalVpdMatters}</b>
          </li>
        </ul>
      </div>

      <div className={chartsContainerClassName}>
        <Tabs>
          <Tabs.TabPane tab="Profit" key={"profitRevenue"}>
            <div className={chartsRowClassName}>
              {/* <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("cumulativeVpdRevenue")}
              >
                {charts["cumulativeVpdRevenue"]}
              </div> */}
              <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("cumulativeVpdProfit")}
              >
                {charts["cumulativeVpdProfit"]}
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Matters" key={"matters"}>
            <div className={chartsRowClassName}>
              <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("mattersCreated")}
              >
                {charts["mattersCreated"]}
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="GPM" key={"gpm"}>
            <div className={chartsRowClassName}>
              <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("grossProfitMargin")}
              >
                {charts["grossProfitMargin"]}
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Agreed & Recovered" key={"agreedRecovered"}>
            <div className={chartsRowClassName}>
              <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("rollingAgreedRate")}
              >
                {charts["rollingAgreedRate"]}
              </div>
              <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("rollingRecoveredRate")}
              >
                {charts["rollingRecoveredRate"]}
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Write Off" key={"writeOff"}>
            <div className={chartsRowClassName}>
              {/* <div className={chartContainerClassName} onClick={() => handleChartClick("writeOff")}>
                {charts["writeOff"]}
              </div> */}
              <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("writeOffPercentage")}
              >
                {charts["writeOffPercentage"]}
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Total Charge" key={"averageTotalCharge"}>
            <div className={chartsRowClassName}>
              <div
                className={chartContainerClassName}
                onClick={() => handleChartClick("averageTotalCharge")}
              >
                {charts["averageTotalCharge"]}
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="AI Insights" key={"aiInsights"}>
            <div style={{ marginTop: 20, width: "1000px" }}>
              {analysisLoading ? (
                <Spin size="large" />
              ) : (
                <>
                  <Typography.Title level={3}>Analysis</Typography.Title>
                  <div
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {aiAnalysis?.split("\n\n").map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                  <Typography.Title level={3}>Recommendations</Typography.Title>
                  <ol
                    style={{
                      paddingLeft: "20px",
                      listStyleType: "decimal",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {aiRecommendations?.map((recommendation, index) => (
                      <li key={index} style={{ marginBottom: "10px" }}>
                        {recommendation.includes(":") ? (
                          <>
                            <Typography.Text strong>
                              {recommendation.split(":")[0]}:
                            </Typography.Text>
                            {recommendation.split(":")[1]}
                          </>
                        ) : (
                          <Typography.Text>{recommendation}</Typography.Text>
                        )}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>

      <Modal open={selectedChart !== null} onCancel={handleModalClose} footer={null} width={"60vw"}>
        <div>
          {selectedChart !== null ? (
            <>
              {charts[selectedChart]}
              <br></br>
              {chartExplanations[selectedChart]}
            </>
          ) : null}
        </div>
      </Modal>
    </>
  );
};
